import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Box, Typography, CircularProgress, Alert, IconButton, Grid, Paper } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useAuth } from "../context/AuthProvider";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
const StatusBox = ({ label, value, color }) => (
  <Paper elevation={2} sx={{ p: 2, borderRadius: 2, textAlign: 'center', backgroundColor: color }}>
    <Typography variant="subtitle2" color="textSecondary">{label}</Typography>
    <Typography variant="h6" color="white">{value}</Typography>
  </Paper>
);

const CoverageReport = () => {
  const { projectId, toolId = '566753372-28b2-4140-026c-215g67jhsb3' } = useParams();
  
  const { organization_id } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [iframeUrl, setIframeUrl] = useState(null);
  const [testReport, setTestReport] = useState(null);
  const [reportContent, setReportContent] = useState('');
  const [currentPath, setCurrentPath] = useState('/index.html');
  const [navigationHistory, setNavigationHistory] = useState(['/index.html']);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [hasConfiguration, setHasConfiguration] = useState(false);
  const iframeRef = useRef(null);
  const runId = useState(null);
  const token = localStorage.getItem("access_token");
  const navigate = useNavigate();
  const baseApiUrl = process.env.REACT_APP_API_NEW_HOST_URL;
  const reportPath = `/athrv/api/v1/platform/reports/cpputest/${projectId}/${toolId}`;

  const getStatusColor = (status) => {
    switch (status) {
      case 'fail': return '#d32f2f';
      case 'pass': return '#2e7d32';
      case 'waiting': return '#f9a825';
      default: return '#1976d2';
    }
  };

  const renderTopStats = () => {
    if (!testReport) return null;

    // Use ok_summary if available, otherwise fall back to error_summary
    const summary = testReport.ok_summary || testReport.error_summary || {};
    const overallStatus = testReport.testcases?.some(tc => tc.status === 'fail') ? 'fail' : 'pass';
    const bannerColor = getStatusColor(overallStatus);

    const hasOkSummary = !!testReport.ok_summary;
    const hasErrorSummary = !!testReport.error_summary;
    const showTestStatus = hasOkSummary || hasErrorSummary;

    let statusText = '';
    if (hasOkSummary) {
      statusText = 'PASS';
    } else if (hasErrorSummary) {
      statusText = 'FAIL';
    }

    return (
      <Box sx={{ mb: 4 }}>
        {/* Status Banner - Only show if we have ok_summary or error_summary */}
        {showTestStatus && (
          <Paper
          elevation={3}
          sx={{
            p: 3,
            mb: 3,
            background: `linear-gradient(135deg, ${bannerColor} 0%, ${bannerColor}dd 100%)`,
            borderRadius: '12px',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h5"
              color="white"
              fontWeight="600"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  borderRadius: '50%',
                  backgroundColor: 'white',
                  opacity: 0.9
                }}
              />
              Test Status: {overallStatus.toUpperCase()}
            </Typography>
            <Typography
              variant="body2"
              color="white"
              sx={{ opacity: 0.9, mt: 0.5 }}
            >
              {testReport.ok_summary ? 'All tests passed successfully' :
                testReport.error_summary ? 'Some tests failed' :
                  'Test analysis completed'}
            </Typography>
          </Box>
          {/* Decorative background element */}
          <Box
            sx={{
              position: 'absolute',
              top: -20,
              right: -20,
              width: 100,
              height: 100,
              borderRadius: '50%',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              zIndex: 0
            }}
          />
        </Paper>
        )}

        {/* Metrics Grid */}
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)',
                color: 'white',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  elevation: 4
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: "1.3rem",
                      fontWeight: 700,
                      color: "#ffffff",
                    }}
                  >
                    Total Tests
                  </Typography>
                  <Typography variant="h4" fontWeight="700">
                    {summary.tests ?? '-'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="h6">📊</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: '12px',
                background: testReport.ok_summary ?
                  'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)' :
                  'linear-gradient(135deg, #f44336 0%, #d32f2f 100%)',
                color: 'white',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  elevation: 4
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: "1.3rem",
                      fontWeight: 700,
                      color: "#ffffff",
                    }}
                  >
                    {testReport.ok_summary ? 'Passed' : 'Failures'}
                  </Typography>
                  <Typography variant="h4" fontWeight="700">
                    {testReport.ok_summary ?
                      (summary.tests - (summary.failures || 0)) :
                      (summary.failures ?? '-')}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="h6">
                    {testReport.ok_summary ? '✅' : '⚠️'}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)',
                color: 'white',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  elevation: 4
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: "1.3rem",
                      fontWeight: 700,
                      color: "#ffffff",
                    }}
                  >
                    Tests Ran
                  </Typography>
                  <Typography variant="h4" fontWeight="700">
                    {summary.ran ?? '-'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="h6">✅</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Paper
              elevation={2}
              sx={{
                p: 3,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #9c27b0 0%, #6a1b9a 100%)',
                color: 'white',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  elevation: 4
                }
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box>
                  <Typography
                    variant="body2"
                    sx={{
                      mb: 1,
                      fontSize: "1.3rem",
                      fontWeight: 700,
                      color: "#ffffff",
                    }}
                  >
                    Duration
                  </Typography>
                  <Typography variant="h4" fontWeight="700">
                    {summary.duration_ms ? `${summary.duration_ms}ms` : '-'}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '8px',
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="h6">⏱️</Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>

        {/* Additional OK Summary Metrics if available */}
        {testReport.ok_summary && (
          <Grid container spacing={3} sx={{ mt: 1 }}>
            {/* <Grid item xs={12} sm={6} md={3}>
              <StatusBox 
                label="Checks" 
                value={testReport.ok_summary.checks || '-'} 
                color="#4caf50" 
              />
            </Grid> */}
            {/* <Grid item xs={12} sm={6} md={3}>
              <StatusBox 
                label="Ignored" 
                value={testReport.ok_summary.ignored || '-'} 
                color="#ff9800" 
              />
            </Grid> */}
            {/* <Grid item xs={12} sm={6} md={3}>
              <StatusBox 
                label="Filtered Out" 
                value={testReport.ok_summary.filtered_out || '-'} 
                color="#607d8b" 
              />
            </Grid> */}
            {/* <Grid item xs={12} sm={6} md={3}>
              <StatusBox 
                label="Success Rate" 
                value={`${Math.round(((testReport.ok_summary.tests - (testReport.ok_summary.failures || 0)) / testReport.ok_summary.tests * 100)}%`} 
                color="#8bc34a" 
              />
            </Grid> */}

            {/* <Grid item xs={12} sm={6} md={3}>
              <StatusBox 
                label="Success Rate" 
                value={
                  testReport.ok_summary && testReport.ok_summary.tests > 0 
                    ? `${Math.round(
                        ((testReport.ok_summary.tests - (testReport.ok_summary.failures || 0)) / 
                        testReport.ok_summary.tests) * 100
                      )}%`
                    : 'N/A'
                } 
                color="#8bc34a" 
              />
          </Grid> */}
          </Grid>
        )}
      </Box>
    );
  };

  // Load requested page with authentication
  const loadPage = async (pagePath, updateHistory = true) => {
    setIsLoading(true);
    try {
      // Normalize the path
      const normalizedPath = pagePath.startsWith('/') ? pagePath : `/${pagePath}`;
      const pageUrl = `${baseApiUrl}${reportPath}${normalizedPath}`;

      const response = await fetch(pageUrl, {
        method: "GET",
        headers: {
          "Authorization": `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        // Get the response text first
        const responseText = await response.text();
        let errorMessage = responseText;
        
        // Try to parse as JSON to check for structured error
        try {
          const errorData = JSON.parse(responseText);
          if (errorData.message) {
            errorMessage = errorData.message;
          } else if (errorData.error) {
            errorMessage = errorData.error;
          }
        } catch (parseError) {
          // If not JSON, use the text directly (it might be a simple string error)
          // Remove quotes if present
          errorMessage = responseText.replace(/^["']|["']$/g, '');
        }
        
        const configRelatedErrors = [
          'unitTestingTools',
          'No unit testing',
          'not configured',
          'no configuration',
          'configure unit testing'
        ];
        
        const isConfigError = configRelatedErrors.some(keyword => 
          errorMessage.toLowerCase().includes(keyword.toLowerCase())
        );
        
        setHasConfiguration(!isConfigError);
        setError(errorMessage);
        return;
      }

      // Get the HTML content
      const htmlContent = await response.text();

      try {
        const jsonCheck = JSON.parse(htmlContent);
        if (jsonCheck.success === false) {
          // Use the backend error message
          const errorMessage = jsonCheck.message || jsonCheck.error || 'An error occurred while loading the coverage report.';
          
          const configRelatedErrors = [
            'unitTestingTools',
            'No unit testing',
            'not configured',
            'no configuration',
            'configure unit testing'
          ];
          
          const isConfigError = configRelatedErrors.some(keyword => 
            errorMessage.toLowerCase().includes(keyword.toLowerCase())
          );
          
          setHasConfiguration(!isConfigError);
          setError(errorMessage);
          return;
        }
      } catch (e) {
        // Not JSON, continue with HTML processing
      }

      // Process the HTML to ensure resources load correctly with authentication
      const processedHtml = await processHtmlResources(htmlContent, normalizedPath);
      setReportContent(processedHtml);
      setCurrentPath(normalizedPath);

      // Update navigation history if needed
      if (updateHistory) {
        // Remove any forward history when navigating to a new page
        const newHistory = [
          ...navigationHistory.slice(0, historyIndex + 1),
          normalizedPath
        ];
        setNavigationHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
      }

      // Clear any previous errors on successful load
      setError(null);
    } catch (err) {
      // Use the actual error message from the catch block
      setError(err.message || 'An unexpected error occurred');
      setHasConfiguration(true); // Assume configuration exists for unexpected errors
    } finally {
      setIsLoading(false);
    }
  };

  // Navigate back in history
  const goBack = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      loadPage(navigationHistory[newIndex], false);
    }
  };

  // Navigate forward in history
  const goForward = () => {
    if (historyIndex < navigationHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      loadPage(navigationHistory[newIndex], false);
    }
  };

  // Return to the parent page
  const returnToParent = () => {
    navigate(-1);
  };

  // Function to process HTML and handle all resources
  const processHtmlResources = async (html, currentPagePath) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Get the directory path for relative URLs
    const dirPath = currentPagePath.substring(0, currentPagePath.lastIndexOf('/') + 1);

    // Process all links to intercept navigation
    const links = Array.from(doc.querySelectorAll('a[href]'));
    for (const link of links) {
      const href = link.getAttribute('href');
      if (href) {
        // Skip external links and anchors
        if (href.startsWith('http') || href.startsWith('//')) {
          // For external links, open in new tab
          link.setAttribute('target', '_blank');
          continue;
        } else if (href.startsWith('#')) {
          // For anchor links, keep them as is
          continue;
        }

        // Create a custom event for our internal navigation
        link.setAttribute('onclick', `
          event.preventDefault();
          window.parent.postMessage({
            type: 'NAVIGATE',
            path: '${href}'
          }, '*');
          return false;
        `);
      }
    }

    // Process CSS links - inline them to avoid cross-origin issues
    const cssLinks = Array.from(doc.querySelectorAll('link[rel="stylesheet"]'));
    for (const cssLink of cssLinks) {
      const href = cssLink.getAttribute('href');
      if (href && !href.startsWith('http') && !href.startsWith('//')) {
        try {
          // Construct absolute URL for CSS
          let absoluteCssUrl;
          if (href.startsWith('/')) {
            absoluteCssUrl = `${baseApiUrl}${href}`;
          } else {
            absoluteCssUrl = `${baseApiUrl}${reportPath}${dirPath}${href}`;
          }

          // Fetch CSS with authentication
          const cssResponse = await fetch(absoluteCssUrl, {
            headers: {
              "Authorization": `Bearer ${token}`,
            },
          });

          if (cssResponse.ok) {
            const cssText = await cssResponse.text();
            // Replace link tag with inline style
            const styleTag = doc.createElement('style');
            styleTag.textContent = processCSS(cssText, absoluteCssUrl);
            cssLink.parentNode.replaceChild(styleTag, cssLink);
          }
        } catch (error) {
          console.error(`Failed to load CSS: ${href}`, error);
        }
      }
    }

    // Process images - including those inside table cells
    // We'll keep track of all images to load them properly
    const imagePromises = [];
    const imageElements = Array.from(doc.querySelectorAll('img'));

    for (const img of imageElements) {
      const src = img.getAttribute('src');
      if (src && !src.startsWith('http') && !src.startsWith('//') && !src.startsWith('data:')) {
        try {
          // Keep original attributes for later
          const originalWidth = img.getAttribute('width');
          const originalHeight = img.getAttribute('height');
          const originalAlt = img.getAttribute('alt');

          // Construct absolute URL for image
          let absoluteImgUrl;
          if (src.startsWith('/')) {
            absoluteImgUrl = `${baseApiUrl}${src}`;
          } else {
            absoluteImgUrl = `${baseApiUrl}${reportPath}${dirPath}${src}`;
          }

          // Create promise for fetching and processing this image
          const imgPromise = (async () => {
            try {
              // Fetch image with authentication
              const imgResponse = await fetch(absoluteImgUrl, {
                headers: {
                  "Authorization": `Bearer ${token}`,
                },
              });

              if (imgResponse.ok) {
                const blob = await imgResponse.blob();
                const reader = new FileReader();

                // Convert to data URL
                return new Promise(resolve => {
                  reader.onloadend = () => {
                    // Set data URL as source
                    img.setAttribute('src', reader.result);

                    // Make sure we keep the original dimensions and attributes
                    if (originalWidth) img.setAttribute('width', originalWidth);
                    if (originalHeight) img.setAttribute('height', originalHeight);
                    if (originalAlt) img.setAttribute('alt', originalAlt);

                    resolve();
                  };
                  reader.readAsDataURL(blob);
                });
              } else {
                throw new Error(`Image response not OK: ${imgResponse.status}`);
              }
            } catch (err) {
              console.error(`Failed to load image: ${src}`, err);
              // Don't throw - keep going with other images
            }
          })();

          imagePromises.push(imgPromise);
        } catch (error) {
          console.error(`Failed to process image: ${src}`, error);
        }
      }
    }

    // Wait for all images to be processed
    await Promise.all(imagePromises);

    // Process background images in inline styles
    const elementsWithStyle = Array.from(doc.querySelectorAll('[style*="background"]'));
    for (const el of elementsWithStyle) {
      const styleAttr = el.getAttribute('style');
      if (styleAttr && styleAttr.includes('url(')) {
        try {
          // Extract URL from inline style
          const urlMatch = styleAttr.match(/url\(['"]?([^'"]+)['"]?\)/);
          if (urlMatch && urlMatch[1] && !urlMatch[1].startsWith('http') && !urlMatch[1].startsWith('data:')) {
            const imgUrl = urlMatch[1];
            // Construct absolute URL for background image
            let absoluteImgUrl;
            if (imgUrl.startsWith('/')) {
              absoluteImgUrl = `${baseApiUrl}${imgUrl}`;
            } else {
              absoluteImgUrl = `${baseApiUrl}${reportPath}${dirPath}${imgUrl}`;
            }

            // Fetch image with authentication
            const imgResponse = await fetch(absoluteImgUrl, {
              headers: {
                "Authorization": `Bearer ${token}`,
              },
            });

            if (imgResponse.ok) {
              const blob = await imgResponse.blob();
              const reader = new FileReader();
              // Convert to data URL and update the style
              await new Promise(resolve => {
                reader.onloadend = () => {
                  el.setAttribute('style', styleAttr.replace(
                    /url\(['"]?([^'"]*?)['"]?\)/,
                    `url(${reader.result})`
                  ));
                  resolve();
                };
                reader.readAsDataURL(blob);
              });
            }
          }
        } catch (error) {
          console.error(`Failed to process background image in style: ${styleAttr}`, error);
        }
      }
    }

    // Process script sources
    const scripts = Array.from(doc.querySelectorAll('script[src]'));
    for (const script of scripts) {
      const src = script.getAttribute('src');
      if (src && !src.startsWith('http') && !src.startsWith('//')) {
        try {
          // Construct absolute URL for script
          let absoluteScriptUrl;
          if (src.startsWith('/')) {
            absoluteScriptUrl = `${baseApiUrl}${src}`;
          } else {
            absoluteScriptUrl = `${baseApiUrl}${reportPath}${dirPath}${src}`;
          }

          // Fetch script with authentication
          const scriptResponse = await fetch(absoluteScriptUrl, {
            headers: {
              "Authorization": `Bearer ${token}`,
            },
          });

          if (scriptResponse.ok) {
            const scriptContent = await scriptResponse.text();
            // Replace script tag with inline script
            const inlineScript = doc.createElement('script');
            inlineScript.textContent = scriptContent;
            script.parentNode.replaceChild(inlineScript, script);
          }
        } catch (error) {
          console.error(`Failed to load script: ${src}`, error);
        }
      }
    }

    // Add script to intercept form submissions and handle XHR requests
    // Also include code to fix table layouts and image display
    const interceptScript = doc.createElement('script');
    interceptScript.textContent = `
      // Intercept form submissions
      document.addEventListener('submit', function(e) {
        e.preventDefault();
        const form = e.target;
        const action = form.getAttribute('action') || '';
        const method = form.getAttribute('method') || 'GET';
        // Notify parent about form submission
        window.parent.postMessage({
          type: 'FORM_SUBMIT',
          path: action,
          method: method,
          formData: new FormData(form)
        }, '*');
        return false;
      });
      
      // Override XMLHttpRequest to handle authenticated requests
      const originalXHROpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function(method, url, ...rest) {
        if (typeof url === 'string' && !url.startsWith('http') && !url.startsWith('//') && !url.startsWith('#')) {
          // Notify parent about XHR request
          window.parent.postMessage({
            type: 'XHR_REQUEST',
            path: url,
            method: method
          }, '*');
          // This is just a placeholder; actual requests will be handled by the parent
        }
        return originalXHROpen.apply(this, arguments);
      };
      
      // Override fetch to handle authenticated requests
      const originalFetch = window.fetch;
      window.fetch = function(url, options = {}) {
        if (typeof url === 'string' && !url.startsWith('http') && !url.startsWith('//') && !url.startsWith('#')) {
          // Notify parent about fetch request
          window.parent.postMessage({
            type: 'FETCH_REQUEST',
            path: url,
            options: options
          }, '*');
          // This is just a placeholder; actual requests will be handled by the parent
        }
        return originalFetch.apply(this, arguments);
      };
      
      // Handle key events for navigation
      document.addEventListener('keydown', function(e) {
        // Alt+Left Arrow for back
        if (e.altKey && e.keyCode === 37) {
          e.preventDefault();
          window.parent.postMessage({ type: 'NAVIGATE_BACK' }, '*');
        }
        // Alt+Right Arrow for forward
        else if (e.altKey && e.keyCode === 39) {
          e.preventDefault();
          window.parent.postMessage({ type: 'NAVIGATE_FORWARD' }, '*');
        }
      });
      
      // Fix table display
      function fixTableDisplay() {
        // Fix for coverage bar tables
        const coverBarTables = document.querySelectorAll('.coverBar table');
        coverBarTables.forEach(table => {
          table.style.borderCollapse = 'collapse';
          table.style.borderSpacing = '0';
          
          // Make sure td elements have proper styling
          const tds = table.querySelectorAll('td');
          tds.forEach(td => {
            if (td.className === 'coverBarOutline') {
              td.style.whiteSpace = 'nowrap';
              td.style.padding = '0';
              td.style.margin = '0';
              
              // Fix images inside coverBarOutline
              const images = td.querySelectorAll('img');
              images.forEach(img => {
                img.style.display = 'inline-block';
                img.style.margin = '0';
                img.style.padding = '0';
                img.style.verticalAlign = 'middle';
                
                // Ensure dimensions are explicit
                if (img.getAttribute('width')) {
                  img.style.width = img.getAttribute('width') + 'px';
                }
                if (img.getAttribute('height')) {
                  img.style.height = img.getAttribute('height') + 'px';
                }
              });
            }
          });
        });
      }
      
      // Run fix on load and after a slight delay (some reports load content dynamically)
      document.addEventListener('DOMContentLoaded', function() {
        fixTableDisplay();
        setTimeout(fixTableDisplay, 500);
      });
      
      // Listen for messages from parent frame to fix display
      window.addEventListener('message', function(event) {
        if (event.data && event.data.type === 'FIX_TABLE_DISPLAY') {
          fixTableDisplay();
        }
      });
    `;
    doc.head.appendChild(interceptScript);

    // Add a base tag to help with relative URLs that we didn't catch
    const baseTag = doc.createElement('base');
    baseTag.href = `${baseApiUrl}${reportPath}${dirPath}`;
    doc.head.insertBefore(baseTag, doc.head.firstChild);

    // Add custom styles to preserve table layouts while fixing display issues
    const styleTag = doc.createElement('style');
    styleTag.textContent = `
      body { margin: 0; padding: 10px; }

      .ruler {
      display: block;
      height: 3px;
      }
      
      /* Table layout preservation */
      table { 
        border-collapse: separate;
      }
      
      /* Coverage bar specific styling */
      .coverBar {
        text-align: center;
      }
      
      .coverBar table {
        border: 0;
        border-collapse: collapse !important;
        border-spacing: 0 !important;
        display: inline-block;
        margin: 0;
        padding: 0;
      }
      
      .coverBarOutline {
        white-space: nowrap;
        padding: 0 !important;
        margin: 0 !important;
        background: none !important;
        display: block;
        line-height: 0px;
        border: 1px solid black;
      }
      
      .coverBarOutline img {
        display: inline-block !important;
        vertical-align: middle !important;
        margin: 0 !important;
        padding: 0 !important;

        max-width: none !important;
      }

 
      
      /* Preserve white-space in pre elements */
      pre, code {
        white-space: pre !important;
      }
    `;
    doc.head.appendChild(styleTag);

    return doc.documentElement.outerHTML;
  };

  // Function to process CSS and fix URLs
  const processCSS = (cssText, baseUrl) => {
    // Extract the base directory from baseUrl
    const baseDir = baseUrl.substring(0, baseUrl.lastIndexOf('/') + 1);

    // Replace relative URLs in CSS with absolute URLs or data URLs
    return cssText.replace(/url\(['"]?(.+?)['"]?\)/g, (match, url) => {
      // Skip data URLs and absolute URLs
      if (url.startsWith('data:') || url.startsWith('http') || url.startsWith('//')) {
        return match;
      }

      // Make URL absolute
      const absoluteUrl = url.startsWith('/')
        ? `${baseApiUrl}${url}`
        : `${baseDir}${url}`;

      return `url("${absoluteUrl}")`;
    });
  };

  // Helper function to normalize paths with ../ references
  const normalizePathWithDotDot = (path) => {
    // Split the path into segments
    const segments = path.split('/').filter(segment => segment !== '');
    const result = [];

    // Process each segment
    for (const segment of segments) {
      if (segment === '..') {
        // Go up one directory level (remove the last segment)
        result.pop();
      } else if (segment !== '.') {
        // Add the segment to the result (ignore '.' segments)
        result.push(segment);
      }
    }

    // Join the segments back into a path
    return '/' + result.join('/');
  };

  // ALL USEEFFECT HOOKS MUST BE CALLED BEFORE ANY CONDITIONAL RETURNS

  // Initial page load
  useEffect(() => {
    loadPage('/index.html', false);
  }, [projectId, toolId]);

  // Fetch test report
  useEffect(() => {
    const fetchTestReport = async () => {
      try {
        const res = await fetch(`${baseApiUrl}/athrv/api/v1/platform/reports/ctest-report-result?project_id=${projectId}`, {
          headers: {
            "Authorization": `Bearer ${token}`,
          },
        });
        
        if (!res.ok) {
          // Try to get the actual error message from backend
          try {
            const errorData = await res.json();
            if (errorData.message) {
              // Check if error is related to configuration
              if (errorData.message.includes('unitTestingTools') || 
                  errorData.message.includes('No unit testing') ||
                  errorData.message.toLowerCase().includes('not configured')) {
                setHasConfiguration(false);
              } else {
                setHasConfiguration(true);
              }
              setError(errorData.message);
            } else if (errorData.error) {
              setError(errorData.error);
            }
          } catch (parseError) {
            // If parsing fails, use generic message
            console.warn("Could not parse error response");
          }
          throw new Error(`Failed to fetch test results: ${res.status}`);
        }
        
        const data = await res.json();
        if (data.unitTesting) {
          setHasConfiguration(true);
          setTestReport(data.unitTesting);
        }
      } catch (err) {
        console.warn("Test report error:", err);
      }
    };
    
    fetchTestReport();
    
    return () => {
      if (iframeUrl) URL.revokeObjectURL(iframeUrl);
    };
  }, [projectId, toolId, token, baseApiUrl]);

  // Set up event listener for messages from iframe
  useEffect(() => {
    const handleIframeMessage = async (event) => {
      if (!event.data || !event.data.type) return;

      switch (event.data.type) {
        case 'NAVIGATE':
          // Handle navigation to a new page
          const newPath = event.data.path;
          // Resolve relative to current path
          let resolvedPath;
          if (newPath.startsWith('/')) {
            resolvedPath = newPath;
          } else {
            const currentDir = currentPath.substring(0, currentPath.lastIndexOf('/') + 1);
            resolvedPath = `${currentDir}${newPath}`;
          }
          // Normalize path to handle ../ references
          const normalizedPath = normalizePathWithDotDot(resolvedPath);
          // Load the new page
          loadPage(normalizedPath);
          break;

        case 'NAVIGATE_BACK':
          goBack();
          break;

        case 'NAVIGATE_FORWARD':
          goForward();
          break;

        case 'FORM_SUBMIT':
          // Handle form submission
          console.log('Form submission intercepted:', event.data);
          // For now, just navigate to the action URL
          if (event.data.path) {
            let formPath = event.data.path;
            // Resolve relative to current path
            if (!formPath.startsWith('/')) {
              const currentDir = currentPath.substring(0, currentPath.lastIndexOf('/') + 1);
              formPath = `${currentDir}${formPath}`;
            }
            loadPage(formPath);
          }
          break;

        case 'XHR_REQUEST':
        case 'FETCH_REQUEST':
          // For simplicity, we're just logging these requests
          // A full implementation would handle these requests and send responses back
          console.log(`${event.data.type} intercepted:`, event.data);
          break;
      }
    };

    // Add event listener
    window.addEventListener('message', handleIframeMessage);

    // Clean up
    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [currentPath, historyIndex, navigationHistory]);

  // Run a script in the iframe to fix table cell images after iframe load
  useEffect(() => {
    if (iframeRef.current && !isLoading) {
      try {
        // Give the iframe content time to fully render
        const fixTablesTimeout = setTimeout(() => {
          const iframe = iframeRef.current;
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage({ type: 'FIX_TABLE_DISPLAY' }, '*');
          }
        }, 300);

        // Try again after a bit longer to catch any late-loading content
        const secondFixTimeout = setTimeout(() => {
          const iframe = iframeRef.current;
          if (iframe && iframe.contentWindow) {
            iframe.contentWindow.postMessage({ type: 'FIX_TABLE_DISPLAY' }, '*');
          }
        }, 1000);

        return () => {
          clearTimeout(fixTablesTimeout);
          clearTimeout(secondFixTimeout);
        };
      } catch (error) {
        console.error('Error running fix script in iframe:', error);
      }
    }
  }, [reportContent, isLoading]);

  // NOW ALL CONDITIONAL RETURNS COME AFTER ALL HOOKS
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4, minHeight: '100vh', backgroundColor: '#f5f7fa' }}>
      {/* Always show stats if available */}
      {renderTopStats()}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <Typography variant="h5" gutterBottom>
          Coverage Report
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton
            onClick={goBack}
            disabled={historyIndex <= 0 || error}
            title="Back"
          >
            <ArrowBackIcon />
          </IconButton>
          <IconButton
            onClick={goForward}
            disabled={historyIndex >= navigationHistory.length - 1 || error}
            title="Forward"
          >
            <ArrowForwardIcon />
          </IconButton>
          <Button
            variant="contained"
            className='athrv-dash-primary-button'
            onClick={() => {
              navigate(`/projects/${projectId}`, { state: { activeTab: 0 } });
            }}
          >
            Return to Project
          </Button>
        </Box>
      </Box>

      {/* Show error alert if there's an error */}
      {error && (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            maxWidth: '600px',
            width: '100%',
            p: 5,
            borderRadius: '16px',
            backgroundColor: '#f8f9fa',
            border: '1px solid #e0e4e8',
            textAlign: 'center',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          {/* Decorative background elements */}
          <Box
            sx={{
              position: 'absolute',
              top: -40,
              right: -40,
              width: 120,
              height: 120,
              borderRadius: '50%',
              backgroundColor: 'rgba(156, 163, 175, 0.08)',
              zIndex: 0
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: -30,
              left: -30,
              width: 100,
              height: 100,
              borderRadius: '50%',
              backgroundColor: 'rgba(156, 163, 175, 0.06)',
              zIndex: 0
            }}
          />

          {/* Content */}
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#fff3cd',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
                border: '3px solid #ffc107',
              }}
            >
              <ErrorOutlineIcon sx={{ fontSize: 40, color: '#ff9800' }} />
            </Box>

            {/* Only show "Configuration Required" if project has no configuration */}
            {!hasConfiguration && (
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  color: '#1a2332',
                  mb: 2,
                  fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
                  letterSpacing: '-0.3px'
                }}
              >
                Configuration Required
              </Typography>
            )}

            {/* Always show the error message */}
            <Typography
              variant="body1"
              sx={{
                color: '#64748b',
                fontSize: '1.05rem',
                lineHeight: 1.7,
                fontFamily: '"Inter", "Segoe UI", "Roboto", sans-serif',
                mb: 3
              }}
            >
              {error}
            </Typography>

            {/* Only show configuration instruction if project has no configuration */}
            {!hasConfiguration && (
              <Box
                sx={{
                  mt: 4,
                  pt: 3,
                  borderTop: '1px solid #e0e4e8'
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#94a3b8',
                    fontSize: '0.9rem',
                    fontStyle: 'italic'
                  }}
                >
                  Please configure unit testing tools to view the coverage report
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </Box>
    )}

      {/* Only show iframe if there's content and no error */}
      {!error && reportContent && (
        <Box
          sx={{
            width: '100%',
            height: '80vh',
            border: '1px solid #ccc',
            borderRadius: '8px',
            overflow: 'auto'
          }}
        >
          <iframe
            ref={iframeRef}
            srcDoc={reportContent}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
            }}
            title="Coverage Report"
            sandbox="allow-same-origin allow-scripts"
          />
        </Box>
      )}
      
    </Box>
  );
};

export default CoverageReport;